import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GithubIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
const terminalLines = [{
  text: '$ npx @deviniter/cli@latest',
  delay: 0
}, {
  text: '',
  delay: 800
}, {
  text: '? Select a template:',
  delay: 1200
}, {
  text: '  ○ NextJS',
  delay: 1400
}, {
  text: '  ● Vite',
  delay: 1500,
  highlight: true
}, {
  text: '  ○ ViteStack',
  delay: 1600
}, {
  text: '',
  delay: 1000
}, {
  text: 'Cloning template...',
  delay: 2200,
  success: true
}, {
  text: 'Installing dependencies...',
  delay: 2800,
  success: true
}, {
  text: 'Project is ready',
  delay: 3400,
  success: true
}, {
  text: '',
  delay: 3800
}, {
  text: '$ cd my-project && npm run dev',
  delay: 4000
}];
export function HeroSection() {
  const [displayedLines, setDisplayedLines] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  useEffect(() => {
    if (displayedLines >= terminalLines.length) {
      setIsTyping(false);
      return;
    }
    const currentLine = terminalLines[displayedLines];
    const timeout = setTimeout(() => {
      if (currentText.length < currentLine.text.length) {
        setCurrentText(currentLine.text.slice(0, currentText.length + 1));
      } else {
        setDisplayedLines(displayedLines + 1);
        setCurrentText('');
      }
    }, currentText.length === 0 ? currentLine.delay : 30);
    return () => clearTimeout(timeout);
  }, [displayedLines, currentText]);
  // Reset animation every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayedLines(0);
      setCurrentText('');
      setIsTyping(true);
    }, 32000);
    return () => clearInterval(interval);
  }, []);
  return <section id="home" className="min-h-screen flex items-center justify-center pt-16 pb-20 px-4 relative overflow-hidden" aria-label="Hero section featuring DevIniter CLI tool" data-id="element-209">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" aria-hidden="true" data-id="element-210" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent" aria-hidden="true" data-id="element-211" />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2334d399' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }} aria-hidden="true" data-id="element-212" />

      <div className="max-w-6xl mx-auto w-full relative z-10" data-id="element-213">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-214">
          {/* Left: Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left" data-id="element-215">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2,
            duration: 0.4
          }} className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6" data-id="element-216">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" aria-hidden="true" data-id="element-217" />
              Open Source Pre-built Templates
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4" data-id="element-218">
              <span className="font-mono text-emerald-400" data-id="element-219">&gt;</span> DevIniter
            </h1>

            <p className="text-xl sm:text-2xl text-slate-300 font-medium mb-4" data-id="element-220">
              Ship faster with pre-built templates
            </p>

            <p className="text-slate-400 text-lg mb-8 max-w-lg mx-auto lg:mx-0" data-id="element-221">
              A collection of blazing fast starter templates to kickstart your
              development journey.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-222">
              <a href="https://github.com/Pet3r1512/DevIniter" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950" data-id="element-223">
                <GithubIcon className="w-5 h-5" aria-hidden="true" data-id="element-224" />
                View on GitHub
              </a>
              <a href="https://deviniter.vercel.app/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg border border-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950" data-id="element-225">
                Try It Now
                <ArrowRightIcon className="w-5 h-5" aria-hidden="true" data-id="element-226" />
              </a>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-8 mt-10 justify-center lg:justify-start" data-id="element-227">
              <div className="text-center" data-id="element-228">
                <div className="text-2xl font-bold text-white" data-id="element-229">900+</div>
                <div className="text-sm text-slate-400" data-id="element-230">Downloads</div>
              </div>
              <div className="w-px h-10 bg-slate-700" aria-hidden="true" data-id="element-231" />
              <div className="text-center" data-id="element-232">
                <div className="text-2xl font-bold text-white" data-id="element-233">3</div>
                <div className="text-sm text-slate-400" data-id="element-234">Templates</div>
              </div>
              <div className="w-px h-10 bg-slate-700" aria-hidden="true" data-id="element-235" />
              <div className="text-center" data-id="element-236">
                <div className="text-2xl font-bold text-white" data-id="element-237">485+</div>
                <div className="text-sm text-slate-400" data-id="element-238">Highest Download</div>
              </div>
              <div className="w-px h-10 bg-slate-700" aria-hidden="true" data-id="element-239" />
              <div className="text-center" data-id="element-240">
                <div className="text-2xl font-bold text-white" data-id="element-241">14+</div>
                <div className="text-sm text-slate-400" data-id="element-242">Stars</div>
              </div>
            </div>
          </motion.div>

          {/* Right: Terminal */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3,
          duration: 0.6
        }} className="relative" data-id="element-243">
            <div className="bg-slate-900 rounded-xl border border-slate-700 shadow-2xl shadow-emerald-500/5 overflow-hidden" data-id="element-244">
              {/* Terminal Header */}
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-800/50 border-b border-slate-700" data-id="element-245">
                <div className="flex gap-2" aria-hidden="true" data-id="element-246">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" data-id="element-247" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" data-id="element-248" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" data-id="element-249" />
                </div>
                <span className="text-xs text-slate-500 font-mono ml-2" data-id="element-250">
                  terminal — zsh
                </span>
              </div>

              {/* Terminal Content */}
              <div className="p-4 sm:p-6 font-mono text-sm sm:text-base min-h-[320px]" role="img" aria-label="Terminal animation showing DevIniter CLI workflow" data-id="element-251">
                {terminalLines.slice(0, displayedLines).map((line, index) => <div key={index} className={`leading-relaxed ${line.success ? 'text-emerald-400' : line.highlight ? 'text-amber-400' : 'text-slate-300'}`} data-id="element-252">
                    {line.success && <CheckIcon className="w-4 h-4 inline mr-1" aria-hidden="true" data-id="element-253" />}
                    {line.text}
                  </div>)}
                {displayedLines < terminalLines.length && <div className="text-slate-300 leading-relaxed" data-id="element-254">
                    {currentText}
                    <span className="cursor-blink text-emerald-400 ml-0.5" aria-hidden="true" data-id="element-255">
                      ▋
                    </span>
                  </div>}
                {!isTyping && <div className="text-slate-300 leading-relaxed mt-1" data-id="element-256">
                    <span className="cursor-blink text-emerald-400" aria-hidden="true" data-id="element-257">
                      ▋
                    </span>
                  </div>}
              </div>
            </div>

            {/* Decorative glow */}
            <div className="absolute -inset-4 bg-emerald-500/10 blur-3xl -z-10 rounded-full" aria-hidden="true" data-id="element-258" />
          </motion.div>
        </div>
      </div>
    </section>;
}